import { autoinject, bindable } from 'aurelia-framework';
import { PubSub } from 'lib/event/PubSub';
import { AsyncErrorHandler, DialogHandler } from 'lib/ui';
import {
  categoryDimensionTypes,
  GridLinePluckingDto,
  GridLineProductionItemDto,
  ItemCertificateDto,
  MooringCertificateListItem,
  MooringProductionTaskType,
  PluckingListDto,
  UnitOfMeasure,
  UpdateGridLineProductionDto,
} from 'models/mooring';
import { ErrorService, MooringCertificateService, ToastService } from 'services';
import { MooringProductionOrderService } from 'services/mooring';
import { HourListDialog } from '../hours/hour-list-dialog';
import './grid-lines-container.scss';
import { I18N } from 'aurelia-i18n';
import { SearchResult } from 'elements/search/search-input';
import { EqualityChecker } from 'lib/helpers';
import { LocalStorageService } from 'lib/serivce/localStorageService';

type GridLineStorage = {
  productionOrderId: number;
  activeGridLine: number;
};

@autoinject
export class GridLinesContainer {
  protected gridLines: GridLineProductionItemDto[] = [];
  protected pluckList: GridLinePluckingDto[] = [];

  protected pluckListRef: EqualityChecker = new EqualityChecker();

  protected completedLinesCount = 0;
  protected totalLinesCount = 0;
  protected percentCompleted = 0;

  @bindable productionOrderId: number;

  private storage = new LocalStorageService<'grid_line'>('mooring-production');

  constructor(
    private productionOrderService: MooringProductionOrderService,
    private toast: ToastService,
    private pubsub: PubSub,
    private dialog: DialogHandler,
    private t: I18N,
    private errorService: ErrorService,
    private certificateService: MooringCertificateService
  ) {}

  protected attached() {
    if (!this.productionOrderId || this.productionOrderId <= 0) {
      throw new Error('productionOrderId is required');
    }
    void this.init();
  }

  async init() {
    await this.getGridLines();
    await this.getGridLinesPluckingList();

    const saved = this.storage.get<GridLineStorage>('grid_line');
    if (saved && saved.productionOrderId === this.productionOrderId) {
      const active = this.gridLines.find((gl) => gl.GridLineId === saved.activeGridLine) || null;
      if (active) {
        this.activateLine(active);
      }
    }
  }

  @AsyncErrorHandler
  async getGridLines() {
    if (!this.productionOrderId || this.productionOrderId <= 0) {
      throw new Error('productionOrderId is required');
    }

    const { data } = await this.productionOrderService.getProductionGridLines(this.productionOrderId);
    this.gridLines = data;

    this.completedLinesCount = this.gridLines.filter((gl) => !!gl.CompletedAt).length;
    this.totalLinesCount = this.gridLines.length;
    this.percentCompleted = this.totalLinesCount > 0 ? Math.round((this.completedLinesCount / this.totalLinesCount) * 100) : 0;

    this.createGridModel();

    if (this.activeLine) {
      const updatedActive = this.gridLines.find((gl) => gl.GridLineId === this.activeLine?.GridLineId) || null;
      this.activateLine(updatedActive);
    }
  }

  @AsyncErrorHandler
  async getGridLinesPluckingList() {
    const { data } = await this.productionOrderService.getGridLinesPluckingList(this.productionOrderId);
    this.pluckList = data;
    this.pluckListRef = new EqualityChecker();
    this.pluckListRef.track(this.pluckList);
  }

  @AsyncErrorHandler
  async updateProductionGridLine(dto: UpdateGridLineProductionDto) {
    window.clearTimeout(this.timer);
    await this.productionOrderService.setGridLineProduction({
      productionOrderId: this.productionOrderId,
      gridLineId: dto.GridLineId,
      dto,
    });

    await this.getGridLines();
    this.toast.showUpdated();
  }

  private getDto(line: GridLineProductionItemDto): UpdateGridLineProductionDto {
    return {
      GridLineId: line.GridLineId,
      Completed: !!line.CompletedAt,
      MountedOnStartBuoy: line.MountedOnStartBuoy ?? false,
      MountedOnEndBuoy: line.MountedOnEndBuoy ?? false,
    };
  }

  @AsyncErrorHandler
  async toggleGridLineCompleted(line: GridLineProductionItemDto) {
    const dto = this.getDto(line);
    dto.Completed = !dto.Completed;
    await this.updateProductionGridLine(dto);
  }

  @AsyncErrorHandler
  async saveMountedOnStartBuoy(line: GridLineProductionItemDto) {
    const dto = this.getDto(line);
    await this.updateProductionGridLine(dto);
  }

  @AsyncErrorHandler
  async saveMountedOnEndBuoy(line: GridLineProductionItemDto) {
    const dto = this.getDto(line);
    await this.updateProductionGridLine(dto);
  }

  activeLine: GridLineProductionItemDto | null = null;
  activePluckList: GridLinePluckingDto | null = null;
  protected activateLine(l: GridLineProductionItemDto) {
    this.activeLine = l;
    this.activePluckList = this.pluckList.find((pl) => pl.GridLineId === l.GridLineId) || null;

    this.storage.set<GridLineStorage>('grid_line', {
      productionOrderId: this.productionOrderId,
      activeGridLine: l.GridLineId,
    });
  }

  private getDimensionString(item: MooringCertificateListItem) {
    const primaryDimensionType = categoryDimensionTypes.find((x) => x.value == item.PrimaryDimensionType);
    const secondaryDimensionType = categoryDimensionTypes.find((x) => x.value == item.SecondaryDimensionType);

    let dimension = `${item.PrimaryDimension} ${this.t.tr(primaryDimensionType?.labelShort)}`;
    if (secondaryDimensionType) {
      dimension += ` / ${item.SecondaryDimension} ${this.t.tr(secondaryDimensionType?.labelShort)}`;
    }

    return dimension;
  }

  protected onCertificateSelected(item: SearchResult<MooringCertificateListItem>, index: number) {
    const line = this.activePluckList.Articles[index];

    if (!line) {
      throw new Error(`No line found for index ${index}`);
    }

    if (!item || !item.return) {
      throw new Error('Invalid certificate item selected');
    }

    if (!line.Certificates) {
      line.Certificates = [];
    }

    line.Certificates.push({
      BatchNumber: item.return.BatchNumber,
      BatchNumberTo: item.return.BatchNumberTo,
      ActualBatchNo: '',
      CertificateId: item.return.Id,
      Quantity: 0, // Default quantity, can be adjusted later
      UnitOfMeasure: UnitOfMeasure.meter,
    });
  }

  @AsyncErrorHandler
  protected async searchForCertificate(articleNumber: string, term: string): Promise<SearchResult[]> {
    try {
      const result = await this.certificateService.seach(term, 0, articleNumber);

      if (!result || !result.length || result === undefined) return [];

      return result.map((x) => ({
        id: x.Id,
        title: `${this.t.tr('general.article')}: ${x.ArticleNo}`,
        meta: `${this.t.tr('general.batchNumber')}: ${x.BatchNumber} ${x.BatchNumberTo ? ' - ' + x.BatchNumberTo : ''}`,
        extra: [
          `${x.CategoryName} - ${x.TypeName} - ${x.ProductTypeName} (${this.getDimensionString(x)})`,
          `${this.t.tr('general.producer')}: ${x.SupplierName?.slice(0, 30)}`,
        ],
        return: x,
      }));
    } catch (error) {
      this.errorService.handleError(error);
    }
    return [];
  }

  protected gridModel: unknown[][];

  protected createGridModel() {
    const columns = Math.max(...this.gridLines.map((gl) => Math.max(gl.StartBuoyColumn, gl.EndBuoyColumn)));
    const rows = Math.max(...this.gridLines.map((gl) => Math.max(gl.StartBuoyRow, gl.EndBuoyRow)));

    // x - x - x
    // |   |   |
    // x - x - x
    // |   |   |
    // x - x - x

    // all bouts and grid lines.
    // B g B g B g B = 3 grid lines, 4 buoys
    const columnEntries = columns + columns - 1;
    const rowEntries = rows + rows - 1;

    const gridData = new Array(rowEntries).fill(null).map(() => new Array(columnEntries).fill(null) as unknown[]);

    for (const gridLine of this.gridLines) {
      const startRowIndex = (gridLine.StartBuoyRow - 1) * 2;
      const startColIndex = (gridLine.StartBuoyColumn - 1) * 2;

      const endRowIndex = (gridLine.EndBuoyRow - 1) * 2;
      const endColIndex = (gridLine.EndBuoyColumn - 1) * 2;

      // Place buoys
      gridData[startRowIndex][startColIndex] = {
        type: 'buoy',
        data: {
          id: gridLine.StartBuoyId,
          name: gridLine.StartBuoyName,
        },
      };

      gridData[endRowIndex][endColIndex] = {
        type: 'buoy',
        data: {
          id: gridLine.EndBuoyId,
          name: gridLine.EndBuoyName,
        },
      };

      // Place grid line
      if (startRowIndex === endRowIndex) {
        // horizontal line
        const midColIndex = (startColIndex + endColIndex) / 2;
        let item = gridData[startRowIndex][midColIndex] as { lines: GridLineProductionItemDto[]; type: string; orientation: string } | null;

        if (!item) {
          item = {
            lines: [],
            type: 'grid-line',
            orientation: 'horizontal',
          };

          gridData[startRowIndex][midColIndex] = item;
        }

        item.lines.push(gridLine);
      } else if (startColIndex === endColIndex) {
        // vertical line
        const midRowIndex = (startRowIndex + endRowIndex) / 2;
        let item = gridData[midRowIndex][startColIndex] as { lines: GridLineProductionItemDto[]; type: string; orientation: string } | null;

        if (!item) {
          item = {
            lines: [],
            type: 'grid-line',
            orientation: 'vertical',
          };
          gridData[midRowIndex][startColIndex] = item;
        }
        item.lines.push(gridLine);
      }
    }

    // find columns between buoys that are empty and fill them with empty grid lines
    for (let r = 0; r < gridData.length; r++) {
      for (let c = 0; c < gridData[r].length; c++) {
        const rc = gridData[r][c];

        if (rc) {
          continue;
        }

        const isHorizontal = r % 2 === 0 && c % 2 === 1;

        const left = c > 0 ? gridData[r][c - 1] : null;
        const right = c < gridData[r].length - 1 ? gridData[r][c + 1] : null;
        const top = r > 0 ? gridData[r - 1][c] : null;
        const bottom = r < gridData.length - 1 ? gridData[r + 1][c] : null;

        if (isHorizontal) {
          const leftIsBuoy = left && (left as any).type === 'buoy';
          const rightIsBuoy = right && (right as any).type === 'buoy';
          const isBetweenBuoys = leftIsBuoy && rightIsBuoy;
          gridData[r][c] = isBetweenBuoys
            ? {
                type: `grid-line`,
                orientation: 'horizontal',
                lines: ['empty'],
              }
            : null;
        } else {
          const topIsBuoy = top && (top as any).type === 'buoy';
          const bottomIsBuoy = bottom && (bottom as any).type === 'buoy';
          const isBetweenBuoys = topIsBuoy && bottomIsBuoy;
          gridData[r][c] = isBetweenBuoys
            ? {
                type: `grid-line`,
                orientation: 'vertical',
                lines: ['empty'],
              }
            : null;
        }

        // Check if left and right are buoys
      }
    }

    this.gridModel = gridData;
  }

  protected removeRow(article: PluckingListDto, certificate: ItemCertificateDto) {
    if (!article || !certificate) {
      throw new Error(`No article or certificates provided`);
    }

    const certificateIndex = article.Certificates.indexOf(certificate);
    if (certificateIndex > -1) {
      article.Certificates.splice(certificateIndex, 1);
    }
  }

  protected async onSavePluckList(pluckList: GridLinePluckingDto) {
    window.clearTimeout(this.timer);
    if (!this.productionOrderId || this.productionOrderId <= 0) {
      throw new Error('productionOrderId is required');
    }

    const dto = pluckList.Articles.map((line) => ({
      ArticleId: line.ArticleId,
      ComponentId: line.ComponentId,
      Certificates: line.Certificates.map((certificate) => ({
        CertificateId: certificate.CertificateId,
        Quantity: certificate.Quantity,
        ActualBatchNo: certificate.ActualBatchNo,
      })),
    }));

    await this.productionOrderService.setGridLinePluckList(this.productionOrderId, dto);
    this.toast.showUpdated();

    await this.getGridLinesPluckingList();
    await this.getGridLines();
  }

  timer = 0;
  startAutoSaverTimer() {
    window.clearTimeout(this.timer);
    if (!this.pluckListRef.hasChanges(this.pluckList)) {
      return;
    }

    this.timer = window.setTimeout(() => {
      void this.onSavePluckList(this.activePluckList);
    }, 5000);
  }

  protected async openHoursDialog() {
    await this.dialog.open(HourListDialog, {
      productionOrderId: this.productionOrderId,
      taskOptions: [MooringProductionTaskType.GridLinePicking, MooringProductionTaskType.GridLineAssembly, MooringProductionTaskType.Splicing],
    });
  }
}
